import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faTriangleExclamation, faMagic } from '@fortawesome/free-solid-svg-icons';
import { workItemsApi, TextAnalysisResponse, WorkItemUpdate } from '../api/workItems';

interface FileImportModalProps {
    open: boolean;
    onClose: () => void;
    onImport: (items: WorkItemUpdate[]) => void;
}

type ImportStage = 'idle' | 'extracting' | 'preview' | 'analyzing' | 'review';

const ACCEPTED_EXTENSIONS = ['.docx', '.xlsx', '.csv'];
const PREVIEW_CHAR_LIMIT = 20000;
const ANALYZE_CHAR_LIMIT = 25000;

const buildDraftFromAnalysis = (analysis: TextAnalysisResponse): WorkItemUpdate => {
    const summary = analysis.summary || '';
    const title = analysis.title || analysis.entity || summary || 'Imported Document';
    return {
        title,
        summary,
        domain: analysis.domain || 'general',
        state: 'TRIAGE',
        needs_action: analysis.decision_needed ?? true,
        company: analysis.entity || undefined,
        status: analysis.status || undefined,
        job_title: analysis.job_title || undefined,
        notes: analysis.next_step || analysis.notes || undefined,
        confidence_score: analysis.confidence,
        due_date: analysis.due_date || undefined,
    };
};

const FileImportModal: React.FC<FileImportModalProps> = ({ open, onClose, onImport }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [stage, setStage] = useState<ImportStage>('idle');
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [fileSize, setFileSize] = useState<number | null>(null);
    const [textDraft, setTextDraft] = useState('');
    const [truncatedAtSource, setTruncatedAtSource] = useState(false);
    const [previewExpanded, setPreviewExpanded] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [analysisResult, setAnalysisResult] = useState<TextAnalysisResponse | null>(null);
    const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set());
    const [includePrimary, setIncludePrimary] = useState(true);

    const canEdit = useMemo(() => {
        return previewExpanded || textDraft.length <= PREVIEW_CHAR_LIMIT;
    }, [previewExpanded, textDraft.length]);

    const isPreviewTruncated = textDraft.length > PREVIEW_CHAR_LIMIT;
    const analysisWillTruncate = textDraft.length > ANALYZE_CHAR_LIMIT;

    const detectableSensitiveData = useMemo(() => {
        // Simple regex for routing/account numbers
        const routingRegex = /\b\d{9}\b/g;
        // This is a bit broad but safer
        const accountRegex = /\b\d{10,16}\b/g;
        return routingRegex.test(textDraft) || accountRegex.test(textDraft);
    }, [textDraft]);

    const handleAutoRedact = () => {
        const redacted = textDraft
            .replace(/\b\d{9}\b/g, '[ROUTING HIDDEN]')
            .replace(/\b\d{10,16}\b/g, '[ACCOUNT HIDDEN]');
        setTextDraft(redacted);
        setError(null);
    };

    const resetState = () => {
        setStage('idle');
        setError(null);
        setFileName(null);
        setFileSize(null);
        setTextDraft('');
        setTruncatedAtSource(false);
        setPreviewExpanded(false);
        setDragActive(false);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (!open) {
            resetState();
        }
    }, [open]);

    const showError = (message: string, nextStage: ImportStage = 'idle') => {
        setError(message);
        setStage(nextStage);
    };

    const handleFileSelected = async (file: File) => {
        const extension = `.${file.name.split('.').pop() || ''}`.toLowerCase();
        if (extension === '.xls') {
            showError('Legacy .xls files are not supported. Please save as .xlsx.');
            return;
        }
        if (!ACCEPTED_EXTENSIONS.includes(extension)) {
            showError('Unsupported file type. Upload .docx, .xlsx, or .csv.');
            return;
        }
        if (file.size === 0) {
            showError('File is empty.');
            return;
        }

        setError(null);
        setFileName(file.name);
        setFileSize(file.size);
        setStage('extracting');

        try {
            const result = await workItemsApi.extractTextFromFile(file);
            setTextDraft(result.text || '');
            setTruncatedAtSource(Boolean(result.truncated));
            setStage('preview');
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            const message = typeof detail === 'string'
                ? detail
                : detail?.message || 'Failed to extract text.';
            showError(message);
        }
    };

    const handleAnalyze = async () => {
        const trimmedText = textDraft.slice(0, ANALYZE_CHAR_LIMIT);
        if (!trimmedText.trim()) {
            showError('Text is empty. Please edit before analyzing.');
            return;
        }

        setError(null);
        setStage('analyzing');
        try {
            const analysis = await workItemsApi.analyzeText({
                text: trimmedText,
                filename: fileName || undefined,
                context: 'general',
            });
            setAnalysisResult(analysis);
            setStage('review');
            if (analysis.suggested_tasks) {
                setSelectedTasks(new Set(analysis.suggested_tasks.map((_, i) => i)));
            }
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            const message = typeof detail === 'string'
                ? detail
                : detail?.message || 'Failed to analyze text.';
            showError(message, 'preview');
        }
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const displayText = canEdit
        ? textDraft
        : textDraft.slice(0, PREVIEW_CHAR_LIMIT);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 4,
                    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45)'
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: 'rgba(59, 130, 246, 0.15)',
                            border: '1px solid rgba(59, 130, 246, 0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#60a5fa',
                        }}
                    >
                        <FontAwesomeIcon icon={faFileUpload} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>
                            Import Office File
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                            Extract text before analysis
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label="Text analysis"
                        sx={{
                            ml: 1,
                            bgcolor: 'rgba(16, 185, 129, 0.15)',
                            color: '#34d399',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            fontWeight: 700,
                        }}
                    />
                </Box>
                <IconButton onClick={handleClose} sx={{ color: 'var(--color-text-muted)' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {stage === 'idle' && (
                    <Box
                        onClick={() => inputRef.current?.click()}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setDragActive(true);
                        }}
                        onDragLeave={(event) => {
                            event.preventDefault();
                            setDragActive(false);
                        }}
                        onDrop={(event) => {
                            event.preventDefault();
                            setDragActive(false);
                            const file = event.dataTransfer.files?.[0];
                            if (file) {
                                handleFileSelected(file);
                            }
                        }}
                        sx={{
                            border: '2px dashed',
                            borderColor: dragActive ? 'var(--color-accent)' : 'var(--color-border)',
                            borderRadius: 3,
                            p: 4,
                            textAlign: 'center',
                            bgcolor: dragActive ? 'var(--color-bg-card-hover)' : 'var(--color-bg-card)',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                        }}
                    >
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'rgba(99, 102, 241, 0.12)',
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                            color: '#818cf8',
                        }}>
                            <FontAwesomeIcon icon={faFileUpload} size="lg" />
                        </Box>
                        <Typography variant="body1" sx={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>
                            Drop a file here or click to browse
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mt: 1 }}>
                            Accepted types: .docx, .xlsx, .csv
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mt: 0.5, display: 'block' }}>
                            Extracts text before analysis
                        </Typography>
                    </Box>
                )}

                {stage === 'extracting' && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <CircularProgress size={40} sx={{ color: 'var(--color-accent)' }} />
                        <Typography variant="body1" sx={{ color: 'var(--color-text-primary)', mt: 2, fontWeight: 600 }}>
                            Extracting text...
                        </Typography>
                        {fileName && (
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                {fileName}
                            </Typography>
                        )}
                    </Box>
                )}

                {stage === 'preview' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>
                                    Preview extracted text
                                </Typography>
                                {fileName && (
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                        {fileName}{fileSize ? ` - ${(fileSize / 1024).toFixed(1)} KB` : ''}
                                    </Typography>
                                )}
                            </Box>
                            {isPreviewTruncated && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setPreviewExpanded((prev) => !prev)}
                                    sx={{
                                        borderColor: 'var(--color-border)',
                                        color: 'var(--color-text-secondary)',
                                        textTransform: 'none',
                                    }}
                                >
                                    {previewExpanded ? 'Show less' : 'Show full text'}
                                </Button>
                            )}
                        </Box>

                        <TextField
                            multiline
                            minRows={10}
                            maxRows={16}
                            value={displayText}
                            onChange={(event) => setTextDraft(event.target.value)}
                            InputProps={{ readOnly: !canEdit }}
                            sx={{
                                bgcolor: 'var(--color-bg-card)',
                                borderRadius: 2,
                                '& .MuiInputBase-root': {
                                    color: 'var(--color-text-primary)',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--color-border)',
                                },
                            }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {truncatedAtSource && (
                                <Chip
                                    size="small"
                                    label="Truncated at 50k characters"
                                    sx={{
                                        bgcolor: 'rgba(245, 158, 11, 0.12)',
                                        color: '#f59e0b',
                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                    }}
                                />
                            )}
                            {analysisWillTruncate && (
                                <Chip
                                    size="small"
                                    label="Analysis uses first 25k characters"
                                    sx={{
                                        bgcolor: 'rgba(99, 102, 241, 0.12)',
                                        color: '#818cf8',
                                        border: '1px solid rgba(99, 102, 241, 0.3)',
                                    }}
                                />
                            )}
                            {detectableSensitiveData && (
                                <Box sx={{
                                    width: '100%', p: 1.5, borderRadius: 2,
                                    bgcolor: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#f87171' }} />
                                        <Typography variant="caption" sx={{ color: '#f87171', fontWeight: 600 }}>
                                            Potential sensitive numbers (Routing/Account) detected.
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={handleAutoRedact}
                                        sx={{ height: 24, fontSize: '0.65rem', textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Mask Automatically
                                    </Button>
                                </Box>
                            )}
                            {!canEdit && (
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                    Expand to edit full text.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {stage === 'review' && analysisResult && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Summary Header */}
                        <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                            <Typography variant="subtitle1" sx={{ color: 'var(--color-text-primary)', fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FontAwesomeIcon icon={faMagic} style={{ color: '#818cf8' }} />
                                Intelligence Report
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                                We detected <strong>{(analysisResult.suggested_tasks?.length || 0) + (includePrimary ? 1 : 0)}</strong> potential items. Review and confirm below.
                            </Typography>
                        </Box>

                        {/* Invoice Intelligence */}
                        {analysisResult.invoice_data && (
                            <Box sx={{ p: 2, borderRadius: 3, border: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-card)' }}>
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Structured Invoice Data
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2, mt: 1.5 }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>Vendor</Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{analysisResult.invoice_data.vendor || 'Unknown'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>Amount</Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--color-accent)', fontWeight: 700 }}>{analysisResult.invoice_data.total_amount || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>Due Date</Typography>
                                        <Typography variant="body2" sx={{ color: '#f87171', fontWeight: 600 }}>{analysisResult.invoice_data.due_date || 'N/A'}</Typography>
                                    </Box>
                                </Box>
                                {analysisResult.sensitive_data_flag && (
                                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, bgcolor: 'rgba(245, 158, 11, 0.1)' }}>
                                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#f59e0b', fontSize: '0.75rem' }} />
                                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                                            Sensitive bank details detected in document.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Task List */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: 'var(--color-text-primary)', fontWeight: 700, mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                                Suggested Items to Create
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>
                                    {selectedTasks.size + (includePrimary ? 1 : 0)} selected
                                </Typography>
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {/* Primary Item */}
                                <Box
                                    onClick={() => setIncludePrimary(!includePrimary)}
                                    sx={{
                                        p: 1.5, borderRadius: 2, border: '1px solid',
                                        borderColor: includePrimary ? 'var(--color-accent)' : 'var(--color-border)',
                                        bgcolor: includePrimary ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                                        cursor: 'pointer', display: 'flex', gap: 2, alignItems: 'flex-start'
                                    }}
                                >
                                    <Box sx={{ mt: 0.5, width: 18, height: 18, borderRadius: 0.5, border: '2px solid', borderColor: includePrimary ? 'var(--color-accent)' : 'var(--color-text-muted)', bgcolor: includePrimary ? 'var(--color-accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {includePrimary && <Box sx={{ width: 8, height: 8, bgcolor: 'white', borderRadius: '50%' }} />}
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{analysisResult.title || 'Extracted Item'}</Typography>
                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>{analysisResult.summary}</Typography>
                                        <Chip size="small" label={analysisResult.domain} sx={{ mt: 1, height: 20, fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700 }} />
                                    </Box>
                                </Box>

                                {/* Split Tasks */}
                                {analysisResult.suggested_tasks?.map((task, idx) => (
                                    <Box
                                        key={idx}
                                        onClick={() => {
                                            const next = new Set(selectedTasks);
                                            if (next.has(idx)) next.delete(idx);
                                            else next.add(idx);
                                            setSelectedTasks(next);
                                        }}
                                        sx={{
                                            p: 1.5, borderRadius: 2, border: '1px solid',
                                            borderColor: selectedTasks.has(idx) ? 'var(--color-accent)' : 'var(--color-border)',
                                            bgcolor: selectedTasks.has(idx) ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                                            cursor: 'pointer', display: 'flex', gap: 2, alignItems: 'flex-start'
                                        }}
                                    >
                                        <Box sx={{ mt: 0.5, width: 18, height: 18, borderRadius: 0.5, border: '2px solid', borderColor: selectedTasks.has(idx) ? 'var(--color-accent)' : 'var(--color-text-muted)', bgcolor: selectedTasks.has(idx) ? 'var(--color-accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {selectedTasks.has(idx) && <Box sx={{ width: 8, height: 8, bgcolor: 'white', borderRadius: '50%' }} />}
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{task.title}</Typography>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>{task.summary}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                <Chip size="small" label={task.domain} sx={{ height: 20, fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700 }} />
                                                {task.due_date && <Chip size="small" label={`Due: ${task.due_date}`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171' }} />}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}

                {error && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(239, 68, 68, 0.12)',
                            border: '1px solid rgba(239, 68, 68, 0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <FontAwesomeIcon icon={faTriangleExclamation} color="#f87171" />
                        <Typography variant="body2" sx={{ color: '#f87171' }}>
                            {error}
                        </Typography>
                    </Box>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS.join(',')}
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                            handleFileSelected(file);
                        }
                    }}
                    style={{ display: 'none' }}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-secondary)',
                        textTransform: 'none',
                    }}
                >
                    Close
                </Button>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {stage === 'preview' && (
                        <Button
                            variant="outlined"
                            onClick={resetState}
                            sx={{
                                borderColor: 'var(--color-border)',
                                color: 'var(--color-text-secondary)',
                                textTransform: 'none',
                            }}
                        >
                            Choose another file
                        </Button>
                    )}
                    {stage === 'review' ? (
                        <Button
                            variant="contained"
                            onClick={() => {
                                const selected: WorkItemUpdate[] = [];
                                if (includePrimary && analysisResult) {
                                    selected.push(buildDraftFromAnalysis(analysisResult));
                                }
                                analysisResult?.suggested_tasks?.forEach((t, i) => {
                                    if (selectedTasks.has(i)) selected.push({
                                        title: t.title,
                                        summary: t.summary,
                                        domain: t.domain,
                                        due_date: t.due_date,
                                        state: 'TRIAGE',
                                        needs_action: true,
                                    } as WorkItemUpdate);
                                });
                                onImport(selected);
                                resetState();
                            }}
                            startIcon={<FontAwesomeIcon icon={faMagic} />}
                            sx={{
                                bgcolor: 'var(--color-accent)',
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'var(--color-accent)' },
                            }}
                        >
                            Create {selectedTasks.size + (includePrimary ? 1 : 0)} Items
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleAnalyze}
                            disabled={stage !== 'preview'}
                            startIcon={<FontAwesomeIcon icon={faMagic} />}
                            sx={{
                                bgcolor: 'var(--color-accent)',
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'var(--color-accent)' },
                            }}
                        >
                            Analyze
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default FileImportModal;
